import React from "react";
import { Link } from "react-router-dom";
import { home, countries, productFamilies } from "@/content/site";
import { ButtonPrimary, ButtonSecondary } from "@/components/NovaKit";

export default function HomeC() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-40 -right-40 w-[800px] h-[800px] rounded-full opacity-30 blur-3xl blob"
             style={{ background: "radial-gradient(circle at 30% 40%, #4F46E5 0%, #06B6D4 40%, transparent 70%)" }} />
        <div className="pointer-events-none absolute top-40 -left-40 w-[600px] h-[600px] rounded-full opacity-25 blur-3xl blob"
             style={{ animationDelay: "4s", background: "radial-gradient(circle at 60% 30%, #F97066 0%, #4F46E5 45%, transparent 70%)" }} />
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 pt-20 md:pt-28 pb-24 relative">
          <div className="up inline-flex items-center gap-2 panel-2 rounded-full px-4 py-2 mb-10 border hairline">
            <span className="w-2 h-2 rounded-full grad-bg" />
            <span className="mono text-[11px] uppercase tracking-[0.18em] text-[#0A0E1A]/80">Now live · Kosovo · Türkiye · United Kingdom</span>
          </div>
          <h1 className="display up text-[3.5rem] sm:text-[5rem] md:text-[6.5rem] lg:text-[8rem] font-bold leading-[0.9] mb-10 max-w-[1300px]" data-testid="home-hero-headline">
            Business infrastructure,
            <br />
            <span className="grad-text">coordinated</span> at last.
          </h1>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <p className="lg:col-span-6 text-lg md:text-2xl leading-[1.4] text-[#0A0E1A]/75 max-w-2xl">
              {home.hero.subheadline}
            </p>
            <div className="lg:col-span-5 lg:col-start-8 flex flex-wrap gap-3">
              <ButtonPrimary to="/solutions" data-testid="cta-explore-solutions">Explore Solutions</ButtonPrimary>
              <ButtonSecondary to="/contact" data-testid="cta-contact">Contact Hubinall</ButtonSecondary>
            </div>
          </div>

          {/* Metric strip */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px panel rounded-2xl overflow-hidden border hairline">
            {[["04","Core product families"],["03","Live regions"],["03","Audience pathways"],["08","Ecosystem partners"]].map(([n,l]) => (
              <div key={l} className="bg-white p-6 md:p-8">
                <div className="display text-4xl md:text-5xl font-bold mb-1 grad-text">{n}</div>
                <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#0A0E1A]/60">{l}</div>
              </div>
            ))}
          </div>

          {/* Trusted by */}
          <div className="mt-16 border-t hairline pt-8 flex flex-col md:flex-row md:items-center gap-6 justify-between">
            <span className="mono text-[10px] uppercase tracking-[0.25em] text-[#0A0E1A]/55">Trusted across the ecosystem</span>
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-base md:text-lg font-semibold text-[#0A0E1A]/65">
              {[...home.partnersPreview.delivery, ...home.partnersPreview.strategic].map(p => (
                <span key={p} className="hover:text-[#4F46E5] transition-colors cursor-default">{p}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AUDIENCE */}
      <section className="border-t hairline panel">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-6">
              <div className="mono text-[11px] uppercase tracking-[0.25em] grad-text font-semibold mb-4">Who it's for</div>
              <h2 className="display text-4xl md:text-6xl font-bold leading-[0.95]">One platform. <span className="grad-text">Three audiences.</span></h2>
            </div>
            <p className="lg:col-span-4 lg:col-start-8 text-base md:text-lg text-[#0A0E1A]/70 leading-relaxed">
              Structured pathways for the teams that move business forward — from founders to institutions.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {home.audienceCards.map((c, i) => (
              <Link to={c.to} key={c.title}
                className="grad-border grad-border-on card-hover p-8 md:p-10 rounded-[20px] group" data-testid={`audience-card-${i}`}>
                <div className="flex items-baseline justify-between mb-8">
                  <span className="mono text-[11px] uppercase tracking-[0.2em] font-bold grad-text">0{i+1}</span>
                  <span className="w-10 h-10 rounded-full grad-bg text-white inline-flex items-center justify-center text-sm group-hover:scale-110 transition-transform">→</span>
                </div>
                <h3 className="display text-2xl md:text-3xl font-bold mb-4 leading-tight">{c.title}</h3>
                <p className="text-sm md:text-base leading-relaxed text-[#0A0E1A]/70">{c.body}</p>
                <div className="mt-8 mono text-[10px] uppercase tracking-[0.25em] text-[#4F46E5] font-semibold">Explore pathway →</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT FAMILIES */}
      <section className="border-t hairline">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-7">
              <div className="mono text-[11px] uppercase tracking-[0.25em] grad-text font-semibold mb-4">The product stack</div>
              <h2 className="display text-4xl md:text-6xl font-bold leading-[0.95]">
                Four core families. <span className="grad-text">One coordinated model.</span>
              </h2>
            </div>
            <p className="lg:col-span-5 text-base md:text-lg text-[#0A0E1A]/70 leading-relaxed">
              {home.whatIs.body}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {productFamilies.map((f, i) => {
              const featured = i === 0 || i === 3;
              return (
                <div key={f.key}
                  className={"card-hover rounded-[20px] p-8 md:p-10 relative overflow-hidden " + (featured ? "grad-bg text-white border hairline" : "grad-border grad-border-on bg-white")}
                  data-testid={`family-${f.key}`}>
                  <div className="flex items-baseline justify-between mb-6">
                    <span className={"mono text-[11px] font-bold tracking-[0.22em] " + (featured ? "text-white/90" : "grad-text")}>FAMILY · 0{i + 1}</span>
                    <span className={"mono text-[10px] tracking-[0.18em] " + (featured ? "text-white/70" : "text-[#0A0E1A]/50")}>{f.modules.length} modules</span>
                  </div>
                  <h3 className={"display text-3xl md:text-4xl font-bold mb-4 leading-tight " + (featured ? "text-white" : "")}>{f.name}</h3>
                  <p className={"text-base leading-relaxed mb-6 " + (featured ? "text-white/85" : "text-[#0A0E1A]/70")}>{f.summary}</p>
                  <ul className="space-y-3 mb-6">
                    {f.modules.map((m) => (
                      <li key={m.name} className={"border-t pt-3 " + (featured ? "border-white/20" : "hairline")}>
                        <div className={"mono text-[10.5px] tracking-[0.12em] uppercase font-bold mb-1 " + (featured ? "text-white" : "text-[#0A0E1A]")}>{m.name}</div>
                        <div className={"text-xs leading-relaxed " + (featured ? "text-white/75" : "text-[#0A0E1A]/65")}>{m.desc}</div>
                      </li>
                    ))}
                  </ul>
                  <Link to="/solutions" className={"mono text-[11px] uppercase tracking-[0.2em] font-semibold flex items-center gap-2 " + (featured ? "text-white" : "text-[#4F46E5]")}>
                    Learn more <span>→</span>
                  </Link>
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-3 mt-12">
            <ButtonPrimary to="/services">View all services</ButtonPrimary>
            <ButtonSecondary to="/platform">Explore the platform</ButtonSecondary>
          </div>
        </div>
      </section>

      {/* REGIONS */}
      <section className="border-t hairline panel">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-6">
              <div className="mono text-[11px] uppercase tracking-[0.25em] grad-text font-semibold mb-4">Where we operate</div>
              <h2 className="display text-4xl md:text-6xl font-bold leading-[0.95]">
                Live in <span className="grad-text">three markets</span>. Built for real operating realities.
              </h2>
            </div>
            <p className="lg:col-span-5 lg:col-start-8 text-base md:text-lg text-[#0A0E1A]/70 leading-relaxed">
              {home.regions.body}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {home.regions.blocks.map(([title, body], i) => (
              <div key={title} className="grad-border grad-border-on card-hover bg-white rounded-[20px] p-8 md:p-10 relative overflow-hidden group" data-testid={`region-card-${i}`}>
                <div className="absolute -top-5 -right-5 display text-[10rem] font-black grad-text opacity-10 group-hover:opacity-25 transition-opacity leading-none">0{i+1}</div>
                <div className="flex items-center gap-2 mb-5 relative">
                  <span className="w-2 h-2 rounded-full bg-[#22C55E] pulse-dot" />
                  <span className="mono text-[10px] uppercase tracking-[0.22em] text-[#0A0E1A]/60">Live region</span>
                </div>
                <h3 className="display text-3xl md:text-4xl font-bold mb-5 relative">{title}</h3>
                <div className="h-px bg-[#E4E6EE] w-12 group-hover:w-24 group-hover:grad-bg transition-all duration-500 mb-5 relative" />
                <p className="text-sm md:text-base leading-relaxed text-[#0A0E1A]/70 relative">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="border-t hairline overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 pt-24 pb-10">
          <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="mono text-[11px] uppercase tracking-[0.25em] grad-text font-semibold mb-4">Ecosystem</div>
              <h2 className="display text-4xl md:text-5xl font-bold leading-[1]">Powered by a <span className="grad-text">growing network</span>.</h2>
            </div>
            <ButtonPrimary to="/partners">View all partners</ButtonPrimary>
          </div>
        </div>
        <div className="border-y hairline py-10 overflow-hidden">
          <div className="flex whitespace-nowrap marquee-c">
            {[...home.partnersPreview.delivery, ...home.partnersPreview.strategic, ...home.partnersPreview.delivery, ...home.partnersPreview.strategic].map((p, i) => (
              <div key={i} className="flex items-center gap-10 px-8">
                <span className="display text-3xl md:text-5xl font-bold">{p}</span>
                <span className="w-2 h-2 rounded-full grad-bg"/>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t hairline">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-20">
          <div className="grad-bg rounded-[28px] p-10 md:p-20 relative overflow-hidden">
            <div className="pointer-events-none absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full bg-white/10 blur-3xl"/>
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-8">
                <div className="mono text-[11px] uppercase tracking-[0.25em] text-white/80 mb-6 font-semibold">Next step</div>
                <h2 className="display text-4xl md:text-6xl lg:text-7xl font-bold leading-[0.95] text-white">
                  Ready to coordinate your next operational layer?
                </h2>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-5">
                <p className="text-base md:text-lg text-white/85 leading-relaxed">{home.finalCta.body}</p>
                <div className="flex flex-col gap-3">
                  <Link to="/contact?form=setup" className="bg-white text-[#0A0E1A] rounded-full px-6 py-4 text-sm font-semibold text-center hover:bg-[#0A0E1A] hover:text-white transition-colors flex items-center justify-between">
                    Start Your Setup <span>→</span>
                  </Link>
                  <Link to="/solutions" className="border border-white/50 text-white rounded-full px-6 py-4 text-sm font-semibold text-center hover:bg-white hover:text-[#0A0E1A] transition-colors flex items-center justify-between">
                    Explore Solutions <span>→</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
